import { useInfiniteQuery } from "@tanstack/react-query";
import { useInView } from "react-intersection-observer";
import {
    fetchClaimAndAccumulatorTableItemsByPk,
    fetchClaimAndAccumulatorTableScan
} from "../../utils/apiUtils";
import SharedTable from "../benefitPlan/SharedTable";
import { useCallback, useEffect, useState } from "react";
import debounce from "lodash.debounce";
import ToggleSwitch from "../shared/ToggleSwitch";
import Select from 'react-select';

const searchOptions = [
    { value: 'pk', label: 'pk (memberUuid)', additional: { pkKey: 'pk', skKey: 'sk' } },
    { value: 'statusIndex', label: 'statusIndex', additional: { pkKey: 'status', skKey: 'sk' } },
]

const ClaimAndAccumulatorTable = ({ memberUuid, autoRefreshEnabled: parentAutoRefreshEnabled }) => {

    const [showMoreColumns, setShowMoreColumns] = useState(false);
    const [selectedSearchOption, setSelectedSearchOption] = useState(null);
    const [searchPkValue, setSearchPkValue] = useState();
    const [decouncedSearchPkValue, setDecouncedSearchPkValue] = useState();
    const [searchSkValue, setSearchSkValue] = useState();
    const [decouncedSearchSkValue, setDecouncedSearchSkValue] = useState();
    const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(parentAutoRefreshEnabled ?? false);

    useEffect(() => {
        setAutoRefreshEnabled(parentAutoRefreshEnabled)
    }, [parentAutoRefreshEnabled])

    useEffect(() => {
        if (memberUuid) {
            setSelectedSearchOption(searchOptions[0])
            setSearchPkValue(memberUuid)
            setDecouncedSearchPkValue(memberUuid)
        }
    }, [memberUuid])

    const { ref, inView } = useInView();

    const queryKey = ['claim-and-accumulator-table', selectedSearchOption?.value, decouncedSearchPkValue, decouncedSearchSkValue];

    const {
        data,
        fetchNextPage,
        isLoading,
        isFetching,
        hasNextPage,
        isFetchingNextPage,
        status,
        error
    } = useInfiniteQuery({
        retry: false,
        refetchInterval: autoRefreshEnabled ? 1000 * 3 : false,
        refetchOnWindowFocus: true,
        queryKey,
        queryFn: ({ pageParam }) => {
            let params = { lastEvaluatedKey: pageParam };

            if (selectedSearchOption?.value && selectedSearchOption.value !== 'pk') {
                params.indexName = selectedSearchOption.value
            }

            if (decouncedSearchPkValue && selectedSearchOption?.value) {
                if (decouncedSearchSkValue) {
                    params.skKey = selectedSearchOption?.additional?.skKey
                    params.skValue = decouncedSearchSkValue
                }

                return fetchClaimAndAccumulatorTableItemsByPk({
                    ...params,
                    pkKey: selectedSearchOption?.additional?.pkKey,
                    pkValue: decouncedSearchPkValue
                })
            }

            return fetchClaimAndAccumulatorTableScan(params)
        },
        select: (data) => {
            return {
                pages: data.pages.map((page) => page.Items?.map(item => {
                    let summary = {};
                    try {
                        const sk = (item.sk || '').toLowerCase();

                        if (sk.startsWith('claim')) {
                            const eob = typeof item.eobClaimDetail === 'string'
                                ? JSON.parse(item.eobClaimDetail)
                                : item.eobClaimDetail;
                            const claim = eob?.claim;
                            summary = {
                                recordKind: 'Claim',
                                claimType: claim?.type,
                                serviceDate: claim?.serviceDateString || claim?.startDate,
                                claimState: claim?.state,
                                inNetwork: claim?.inNetwork,
                                coverageAmount: claim?.coverageAmount || claim?.requestedFeeAmount,
                                supplierName: claim?.supplier?.name || claim?.hcfa1500Info?.supplierInformation?.supplierBillingName,
                            };
                        } else if (sk === 'accumulator' || sk.startsWith('accum')) {
                            const details = typeof item.accumulatorDetails === 'string'
                                ? JSON.parse(item.accumulatorDetails)
                                : item.accumulatorDetails;
                            const plan = Array.isArray(details) ? details[0]?.benefitPlanAccum : null;
                            const accums = plan?.accumulators || [];
                            summary = {
                                recordKind: 'Accumulator',
                                planName: plan?.planName || plan?.enrolledPlanHccId,
                                accumCount: accums.length,
                            };
                        }
                    } catch {
                        // keep row as-is if nested payload isn't parseable
                    }
                    return { ...item, ...summary };
                })).flatMap((x) => x),
                pageParams: data.pageParams,
            };
        },
        getNextPageParam: (lastPage) => {
            return lastPage.LastEvaluatedKey ? encodeURIComponent(JSON.stringify(lastPage.LastEvaluatedKey)) : null
        },
        staleTime: 0,
        enabled: true
    })

    useEffect(() => {
        if (inView) {
            fetchNextPage();
        }
    }, [inView]);

    const debouncedChangeHandler = useCallback(
        debounce((event) => {
            setDecouncedSearchPkValue(event.target.value?.trim());
        }, 500),
        []
    );

    const debouncedSkChangeHandler = useCallback(
        debounce((event) => {
            setDecouncedSearchSkValue(event.target.value?.trim());
        }, 500),
        []
    );

    const items = data?.pages.flat();

    let columns = ['sk', 'externalId', 'recordKind', 'claimHccId', 'planName', 'claimType', 'serviceDate', 'accumCount', 'coverageAmount', 'updatedAt'];

    if (showMoreColumns) {
        columns = [
            ...columns,
            'pk', 'claimState', 'inNetwork', 'supplierName', 'version', 'createdAt'
        ];
    }

    return (
        <div className="container">
            {!memberUuid &&
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <div>
                        <p className="title">Claim And Accumulator Table</p>
                        <p className="sub-title">pk: memberUuid -- sk: claim#... | accumulator | GSI: statusIndex</p>
                    </div>
                    <div>
                        <ToggleSwitch
                            label={`Auto Refresh`}
                            onToggle={(val) => setAutoRefreshEnabled(val)}
                            initialState={autoRefreshEnabled}
                        />
                    </div>
                </div>
            }

            {!memberUuid &&
                <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1rem 1rem 0rem' }}>
                        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', width: '100%', flexWrap: 'wrap' }}>
                            <Select
                                name={'searchOptions'}
                                className="react-select-container large-input"
                                classNamePrefix="react-select"
                                options={searchOptions}
                                value={selectedSearchOption}
                                onChange={(option) => {
                                    setSelectedSearchOption(option);
                                    setSearchPkValue('');
                                    setDecouncedSearchPkValue('');
                                    setSearchSkValue('');
                                    setDecouncedSearchSkValue('');
                                }}
                            />

                            {selectedSearchOption?.value && <>
                                <input
                                    style={{ fontSize: "1rem", minWidth: "250px", padding: "1rem", borderRadius: ".4rem", border: "1px solid var(--lighterGrey)", outline: "none" }}
                                    type="text"
                                    id={"search"}
                                    placeholder={selectedSearchOption?.additional?.pkKey}
                                    autoComplete="off"
                                    onChange={(e) => {
                                        setSearchPkValue(e.target.value);
                                        debouncedChangeHandler(e);
                                    }}
                                    value={searchPkValue}
                                />
                                <input
                                    style={{ fontSize: "1rem", minWidth: "250px", padding: "1rem", borderRadius: ".4rem", border: "1px solid var(--lighterGrey)", outline: "none" }}
                                    type="text"
                                    id={"sk"}
                                    placeholder={selectedSearchOption?.additional?.skKey}
                                    autoComplete="off"
                                    onChange={(e) => {
                                        setSearchSkValue(e.target.value);
                                        debouncedSkChangeHandler(e);
                                    }}
                                    value={searchSkValue}
                                />
                            </>}
                        </div>
                    </div>

                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0rem 1rem 1rem 0rem" }}>
                        <p
                            style={{ cursor: "pointer", color: "var(--red)" }}
                            onClick={() => {
                                setSelectedSearchOption(null)
                                setSearchPkValue('');
                                setDecouncedSearchPkValue('');
                                setSearchSkValue('');
                                setDecouncedSearchSkValue('')
                            }}
                        >
                            Clear filters
                        </p>
                        <p style={{ cursor: "pointer", color: "var(--green)" }} onClick={() => setShowMoreColumns(!showMoreColumns)}>
                            {showMoreColumns ? "Less Columns" : "More Columns"}
                        </p>
                    </div>
                </>
            }

            {status === "loading" ? (
                <p>Loading...</p>
            ) : status === "error" ? (
                <span className='danger'>Error: {error.message}</span>
            ) : (
                <>
                    <SharedTable
                        type={"claim-and-accumulator-table"}
                        columns={columns}
                        items={items}
                        activeDraftId={null}
                        hasNextPage={hasNextPage}
                        isFetchingNextPage={isFetchingNextPage}
                        fetchNextPage={fetchNextPage}
                        isFetching={isFetching}
                        isLoading={isLoading}
                        ref={ref}
                    />
                </>
            )}
        </div>
    )
}

export default ClaimAndAccumulatorTable
